<script setup lang="ts">
import type { AwardConfig } from '~/types/appTypes'

const app = useNuxtApp()
const route = useRoute()
const kijidasConfig = useAppConfig().kijidas
const awardConfigList = useAppConfig().app.award
const awardConfig = awardConfigList[route.params.award as string] as AwardConfig
const storeIndex = useStoreIndex()
const storeArticleList = useStoreArticleList()
const storeArticleDetail = useStoreArticleDetail()
const storeMuseumChar = useStoreMuseumChar()

definePageMeta({ layout: 'layout-side-default' })

const baseTitle = awardConfig.baseTitle
const breadcrumbTitle = awardConfig.breadcrumbTitle || baseTitle
let headTitle = ''
let curBreadcrumbs = ''
let headDescription = ''

const year = app.$anyToInteger(route.params.year)

const pageData = {
  resultStaticItem: null as Article,
  resultHistory: [],
  items: [] as MuseumCharItem[],
}

const refData = reactive({
  entryMode: null,
  isResult: false,
})

const setHead = () => {
  headTitle = `${baseTitle}${year}`
  headDescription = awardConfig.description
  // ぱんくず作成
  storeIndex.setBreadCrumbs([
    {
      text: breadcrumbTitle,
      disabled: false,
      to: {
        name: 'award',
        params: { award: awardConfig.awardSlug },
      },
    },
    {
      text: headTitle,
      disabled: false,
      to: {
        name: 'award-year',
        params: {
          award: awardConfig.awardSlug,
          year: year,
        },
      },
    },
    {
      text: curBreadcrumbs,
      disabled: true,
    },
  ])

  const headInput = app.$makeMeta({
    title: headTitle,
    description: headDescription,
  })
  useHead(headInput)
}

const initPage = async () => {
  //  museum-chara カテゴリ取得
  const [_resSite, _resMaster, resMCCat] = await Promise.all([
    storeIndex.fetchSite(),
    storeIndex.fetchMaster(),
    storeArticleList.fetchCategories(awardConfig.awardSlug),
  ])

  //  このアワードを表示していいか
  const entryMode = app.$isAwardEntry(awardConfig, year)
  if (entryMode === null || entryMode === 'before') {
    //  表示できない
    throw createError({
      statusCode: 999,
      statusMessage: 'このアワードは現在表示できません',
    })
    return
  }

  //  このアワードの結果staticページがあるか
  const resultSlug = app.$isAwardResult(awardConfig, year)
  if (resultSlug) {
    curBreadcrumbs = '投票結果発表'
  } else {
    curBreadcrumbs = '途中経過'
  }

  const mcCats = resMCCat.data.Categories
  if (!mcCats?.length) {
    throw createError({
      statusCode: 999,
      statusMessage: `カテゴリ取得エラー(${awardConfig.awardSlug})`,
    })
    return
  }

  //  museum-chara カテゴリから xxxx年のカテゴリid取得
  const charaCatSlug = awardConfig.categorySlugPrefix + year
  const charaCatId = app.$getCategoryBySlug(mcCats, charaCatSlug)

  //  staticページ と キャラ取得
  const [resStatic, resMuseumCharList] = await Promise.all([
    resultSlug
      ? storeArticleDetail.fetchDetail({
          fetchParams: {
            articleTypeSlug: 'static',
            articleSlugOrId: resultSlug as string,
          },
        })
      : null,
    storeMuseumChar.fetchCharaList(kijidasConfig, awardConfig.awardSlug, year, charaCatId),
  ])
  pageData.resultStaticItem = resStatic ? resStatic.data.article : null
  pageData.items = resMuseumCharList

  //  過去の結果ページslugの取得
  const resultMaster = storeIndex.state.master[awardConfig.master.result]

  const resultHistory = []
  if (resultMaster?.details?.length) {
    //  リンクを作成
    resultMaster.details.forEach((result) => {
      const item = {
        year: result.text,
        to: {
          name: `award-year-result`,
          params: {
            award: awardConfig.awardSlug,
            year: result.text,
          },
        },
      }
      resultHistory.push(item)
    })
    pageData.resultHistory = resultHistory
  }

  refData.isResult = !!resultSlug
}
await initPage()
setHead()
</script>

<template>
  <div class="p-articlePage">
    <div
      v-if="refData.isResult"
      id="staticBody"
      v-html="app.$getArticleBody(pageData.resultStaticItem)"
    />
    <div
      v-else
    >
      <h1 class="c-heading_page">
        {{ headTitle }}
      </h1>
      <div
        v-if="refData.entryMode === 'after'"
        class="p-museumChar_pastEntry"
      >
        投票は締め切りました。たくさんの応援ありがとうございました。
      </div>
    </div>

    <div class="p-museumChar">
      <div class="p-museumChar_resultHistory">
        <div class="p-museumChar_head">
          <img
            class="p-museumChar_head_icon"
            src="@/assets/images/icon-title.png"
          >
          <span
            class="p-museumChar_head_title"
          >
            過去の結果
          </span>
        </div>
        <div class="p-museumChar_resultHistory_years">
          <template
            v-for="result in pageData.resultHistory"
            v-bind:key="result.id"
          >
            <nuxt-link
              class="p-museumChar_resultHistory_year"
              v-bind:to="result.to"
            >
              {{ result.year }}
            </nuxt-link>
          </template>
        </div>
      </div>

      <template
        v-if="pageData.items"
      >
        <div class="p-museumChar_head">
          <img
            class="p-museumChar_head_icon"
            src="@/assets/images/icon-title.png"
          >
          <span
            v-if="refData.isResult"
            class="p-museumChar_head_title"
          >
            投票結果発表
          </span>
          <span
            v-else
            class="p-museumChar_head_title"
          >
            途中経過
          </span>
        </div>

        <div class="p-museumChar_charaList">
          <AwardItem
            v-for="(item, index) in pageData.items"
            v-bind:key="item.id"
            disp-count
            v-bind:result="refData.isResult"
            v-bind:item="item"
            v-bind:rank="index + 1"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<style lang = "scss" scoped>
@use "@/assets/css/foundation/mixin/responsive";

#staticBody {
  :deep(h1) {
    margin-bottom: 20px;
    color: #252525;
    font-family: "Noto Sans JP", sans-serif;
    font-size: 26px;
    font-weight: 400;
    line-height: 1.3;
  }

  :deep(img[width="644"]) {
    width: 100%;
  }
}

.p-museumChar {
  &_head {
    display: flex;
    align-items: flex-start;
    margin-top: 30px;
    margin-bottom: 10px;
    padding-bottom: 12px;
    border-bottom: solid 1px var(--g_color_border_gray);

    &_icon {
      margin-top: 3px;
      margin-right: 15px;

      @include responsive.mq_down(sp) {
        width: 20px;
        height: 20px;
      }
    }

    &_title {
      display: inline-block;
      position: relative;
      width: 100%;
      margin-right: 10px;
      font-family: var(--g_font_family_SawarabiGothic);
      font-size: 20px;
      font-weight: bold;
      line-height: 1.5;

      @include responsive.mq_down(sp) {
        font-size: 18px;
      }
    }
  }

  &_pastEntry {
    margin: 20px 0;
    padding: 20px;
    background-color: #ddd;
    color: var(--color_error);
    font-size: 20px;
    font-weight: bold;
    text-align: center;

    @include responsive.mq_down(sp) {
      padding: 15px;
      font-size: 16px;
    }
  }

  &_resultHistory {
    margin-bottom: 30px;

    &_years {
      display: flex;
      flex-wrap: wrap;
    }

    &_year {
      width: 62px;
      height: 38px;
      margin-top: 10px;
      margin-right: 5px;
      border-radius: 5px;
      background-color: v-bind("awardConfig.primaryColor");
      color: #fff;
      font-size: 18px;
      font-weight: bold;
      line-height: 38px;
      text-align: center;

      &:hover {
        opacity: 0.7;
        text-decoration: none;
      }
    }
  }

  &_charaList {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    margin: 0 -8px;
  }
}
</style>
